import React, { useEffect, useState } from "react";
import axios from "axios";
import { Music as MusicIcon, Headphones, ExternalLink, Heart } from "lucide-react";
import { useBookmarks } from "../hooks/useBookmarks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// inline brand glyphs (kept tiny so we don't pull a brand pack)
const SpotifyMark = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.52 17.34c-.24.36-.66.48-1.02.24-2.82-1.74-6.36-2.1-10.56-1.14-.42.12-.78-.18-.9-.54-.12-.42.18-.78.54-.9 4.56-1.02 8.52-.6 11.64 1.32.42.18.48.66.3 1.02zm1.44-3.3c-.3.42-.84.6-1.26.3-3.24-1.98-8.16-2.58-11.94-1.38-.48.12-1.02-.12-1.14-.6-.12-.48.12-1.02.6-1.14 4.38-1.32 9.78-.66 13.5 1.62.36.18.54.78.24 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.3c-.6.18-1.2-.18-1.38-.72-.18-.6.18-1.2.72-1.38 4.26-1.26 11.28-1.02 15.72 1.62.54.3.72 1.02.42 1.56-.3.42-1.02.6-1.56.3z" />
    </svg>
);
const AppleMark = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
);
const YouTubeMark = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .5-5.8 31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
    </svg>
);

const PlatformLink = ({ href, icon: Icon, label, color }) => {
    if (!href) return null;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`music-link-${label.toLowerCase()}`}
            className="group inline-flex items-center gap-2 rounded-full px-4 h-10 bg-white border border-border hover:border-transparent hover:text-white transition-all"
            style={{ "--hover": color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = color)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
        >
            <Icon className="w-4 h-4" />
            <span className="text-xs font-medium">{label}</span>
            <ExternalLink className="w-3 h-3 opacity-60" />
        </a>
    );
};

export const Music = () => {
    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isMarked, toggle } = useBookmarks();

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/music`)
            .then((r) => mounted && setTracks(r.data || []))
            .catch(() => mounted && setTracks([]))
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    if (!loading && tracks.length === 0) return null;

    return (
        <section
            id="music"
            data-testid="music-section"
            className="relative py-24 md:py-36 bg-gradient-to-b from-[#F4ECF7] via-lilac-50/40 to-[#F4ECF7]"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="eyebrow text-gold-deep mb-4 flex items-center gap-2">
                            <MusicIcon className="w-3.5 h-3.5" /> Healing Music
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-lilac-900 leading-tight">
                            Music for the <em className="italic gold-text">becoming</em>.
                        </h2>
                        <p className="mt-5 text-lilac-700 max-w-xl leading-relaxed">
                            Coach Melissa's healing music — soundscapes for surrender, worship, and the
                            quiet work of becoming. Stream on every major platform.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-80 rounded-3xl bg-lilac-100/50 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tracks.map((t) => (
                            <article
                                key={t.id}
                                data-testid={`music-card-${t.id}`}
                                className="group relative rounded-3xl bg-white border border-border overflow-hidden hover:shadow-[0_24px_64px_rgba(43,29,53,0.10)] transition-all"
                            >
                                <div className="relative aspect-square overflow-hidden">
                                    <img
                                        src={t.cover_url}
                                        alt={t.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-lilac-900/60 via-transparent to-transparent" />
                                    <button
                                        onClick={() => toggle("tracks", t.id)}
                                        data-testid={`bookmark-track-${t.id}`}
                                        aria-label={isMarked("tracks", t.id) ? "Remove from My Becoming" : "Save to My Becoming"}
                                        className="absolute top-4 left-4 grid place-items-center w-9 h-9 rounded-full bg-white/90 backdrop-blur hover:bg-white transition-colors"
                                    >
                                        <Heart
                                            className={`w-4 h-4 ${isMarked("tracks", t.id) ? "text-gold-deep" : "text-lilac-700"}`}
                                            fill={isMarked("tracks", t.id) ? "currentColor" : "none"}
                                        />
                                    </button>
                                    {/* spinning vinyl mark */}
                                    <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/90 backdrop-blur grid place-items-center shadow-md">
                                        <span className="w-3 h-3 rounded-full bg-gold" />
                                    </div>
                                    <div className="absolute bottom-4 left-4 right-4 text-white">
                                        <div className="eyebrow text-white/80">
                                            {t.track_type || (t.spotify_url || t.apple_url || t.youtube_url ? "Album" : "In Studio")}
                                        </div>
                                        <div className="font-display text-2xl leading-tight mt-1 line-clamp-2">
                                            {t.title}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    {t.description && (
                                        <p className="text-sm text-lilac-700 leading-relaxed line-clamp-2">
                                            {t.description}
                                        </p>
                                    )}
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        <PlatformLink
                                            href={t.spotify_url}
                                            icon={SpotifyMark}
                                            label="Spotify"
                                            color="#1DB954"
                                        />
                                        <PlatformLink
                                            href={t.apple_url}
                                            icon={AppleMark}
                                            label="Apple"
                                            color="#FA243C"
                                        />
                                        <PlatformLink
                                            href={t.youtube_url}
                                            icon={YouTubeMark}
                                            label="YouTube"
                                            color="#FF0000"
                                        />
                                        {!t.spotify_url && !t.apple_url && !t.youtube_url && (
                                            <span className="inline-flex items-center gap-2 rounded-full px-4 h-10 bg-lilac-100 text-lilac-700 text-xs">
                                                <Headphones className="w-3.5 h-3.5" />
                                                {t.track_type === "In Studio" ? "More music coming soon" : "Links coming soon"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
